import React from "react";
import { SumRow } from "./proposalShared";
import { Button } from "../ui/button";
import { Clock, Loader2, Ticket, X } from "lucide-react";
import { Input } from "../ui/input";
import { euro, fmtDate } from "../../lib/format";

export default function ProposalSummaryBody({ applyPromo, cur, earlyActive, earlyVariants, locked, promo, promoBusy, promoInput, quote, removePromo, setPromoInput, totals }) {
  return (
    <div className="p-6 space-y-3 text-sm">
      <SumRow label="Imponibile servizi" value={euro(totals.imponibile, cur)} testid="public-summary-subtotal" />
      {totals.scontoTotale > 0 && (
        <SumRow label="Sconto applicato" value={`- ${euro(totals.scontoTotale, cur)}`} testid="public-summary-sconto" muted />
      )}
      {totals.contributo > 0 && (
        <SumRow label={totals.contributoLabel} value={euro(totals.contributo, cur)} testid="public-summary-inps" muted />
      )}
      {totals.ivaBreakdown.map((b) => (
        <SumRow key={b.rate} label={`IVA ${b.rate}%`} value={euro(b.iva, cur)} testid={`public-summary-iva-${b.rate}`} muted />
      ))}
      {totals.bollo > 0 && (
        <SumRow label="Marca da bollo" value={euro(totals.bollo, cur)} testid="public-summary-bollo" muted />
      )}
      <div className="border-t border-border pt-3">
        <SumRow label="Totale documento" value={euro(totals.totaleLordo, cur)} testid="public-summary-total-gross" />
      </div>
      {totals.ritenuta > 0 && (
        <SumRow label={`Ritenuta d'acconto ${totals.ritenutaRate}%`} value={`- ${euro(totals.ritenuta, cur)}`} testid="public-summary-ritenuta" muted />
      )}
      <div className="border-t border-border pt-3">
        <div className="flex items-center justify-between">
          <span className="font-bold text-slate-900">Netto a pagare</span>
          <span data-testid="public-summary-netto" className="font-mono text-lg font-extrabold text-slate-900">{euro(totals.netto, cur)}</span>
        </div>
      </div>

      {!locked && earlyActive && earlyVariants && earlyVariants.full > earlyVariants.early && (
        <div data-testid="early-bird-double-summary" className="border-t border-border pt-4 space-y-2">
          <div data-testid="early-warning-box" className="rounded-lg bg-amber-50 border border-amber-300 px-3 py-2.5 text-xs text-amber-800 leading-snug">
            <strong>ATTENZIONE:</strong> il prezzo scontato di <strong>{euro(earlyVariants.early, cur)}</strong> è valido SOLO se il pagamento avviene entro il <strong>{quote.early_discount_deadline ? fmtDate(quote.early_discount_deadline) : "termine indicato"}</strong>. Dopo tale data l'importo dovuto sarà il prezzo pieno di <strong>{euro(earlyVariants.full, cur)}</strong>.
          </div>
          <div className="rounded-lg bg-indigo-600 text-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-200">
              Totale scontato {quote.early_discount_deadline ? `(se accetti entro il ${fmtDate(quote.early_discount_deadline)})` : "(offerta a tempo)"}
            </p>
            <p data-testid="early-discounted-total" className="font-mono text-2xl font-extrabold mt-0.5">{euro(earlyVariants.early, cur)}</p>
          </div>
          <div className="rounded-lg border border-slate-200 px-4 py-2.5 flex items-center justify-between">
            <p className="text-xs text-slate-500">Totale a prezzo pieno {quote.early_discount_deadline ? `(dopo il ${fmtDate(quote.early_discount_deadline)})` : ""}</p>
            <p data-testid="early-full-total" className="font-mono text-sm font-semibold text-slate-500 line-through">{euro(earlyVariants.full, cur)}</p>
          </div>
        </div>
      )}

      {!locked && (
        <div data-testid="promo-code-section" className="border-t border-border pt-4">
          {promo ? (
            <div data-testid="promo-applied" className="flex items-center justify-between gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <Ticket style={{ width: 16, height: 16 }} className="text-emerald-600 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-emerald-800 truncate">Codice "{promo.code}" applicato</p>
                  <p className="text-xs text-emerald-600">
                    {promo.discount_type === "percent" ? `-${promo.discount_value}%` : `- ${euro(promo.discount_value, cur)}`}
                    {totals.scontoPromo > 0 ? ` · risparmi ${euro(totals.scontoPromo, cur)}` : ""}
                  </p>
                </div>
              </div>
              <button data-testid="promo-remove-button" onClick={removePromo} className="text-emerald-700 hover:text-emerald-900 shrink-0">
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>
          ) : (
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1.5">
                <Ticket style={{ width: 13, height: 13 }} /> Hai un codice sconto?
              </p>
              <div className="flex gap-2">
                <Input
                  data-testid="promo-code-input"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === "Enter") applyPromo(); }}
                  placeholder="Es. BENVENUTO10"
                  className="font-mono uppercase"
                />
                <Button data-testid="promo-apply-button" onClick={applyPromo} disabled={promoBusy || !promoInput.trim()} variant="outline" className="shrink-0 font-semibold">
                  {promoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Applica"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}


      {totals.ritenuta > 0 && (
        <p data-testid="public-ritenuta-note" className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 leading-snug">
          Ritenuta d'acconto {totals.ritenutaRate}% ({euro(totals.ritenuta, cur)}) a carico del committente da versare tramite F24.
        </p>
      )}

      <div className="border-t border-border pt-4">
        <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Rate di pagamento (sul netto)</p>
        <div className="space-y-2.5">
          {totals.installments.map((inst, i) => (
            <div key={i} className="flex items-center justify-between">
              <span className="text-slate-600">{inst.label}</span>
              <span className="font-mono font-semibold text-slate-900">{euro(inst.amount, cur)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-border pt-4 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-slate-600">
          <Clock style={{ width: 15, height: 15 }} /> Tempistica stimata
        </span>
        <span className="font-mono font-semibold text-slate-900">{totals.deliveryDays} giorni lavorativi</span>
      </div>
    </div>
  );
}
